import api from '../../../config/axios-interceptor';


export const markChatAsRead = async (id) => {
    try {
        return await api.put(`/api/v1/chat/mark-as-read/${id}`);
    } catch (error) {
        console.error('❌ Error marking chat as read:', error);
        throw error;
    }
};


export const closeChat = async (id) => {
    try {
        return await api.put(`/api/v1/chat/close/${id}`);
    } catch (error) {
        console.error('❌ Error closing chat:', error);
        throw error;
    }
};

export const createMessageApi = async (dto) => {
    try {
        return await api.post("/api/v1/message/create", dto);
    } catch (error) {
        console.error('❌ Error sending chat:', error);
        throw error;
    }
};

export async function uploadFileToMinio(file) {
    const form = new FormData();
    form.append("file", file);

    return await api.post("/api/v1/file/upload", form, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
}


/**
 * Loads a chat transcript over REST.
 *
 * The console used to obtain messages ONLY by publishing /app/message/list over STOMP, so opening a
 * chat — especially straight from the history page on a fresh load — rendered nothing until the
 * WebSocket had finished its handshake, CONNECT and round-trip. That is the multi-second blank the
 * operator sees. This gives an immediate paint; the STOMP push then keeps it live and replaces it.
 */
export const fetchMessagesByChatId = async (chatId) => {
    try {
        // NEWEST first, then reversed back into chronological order for rendering.
        //
        // This used to ask for `createdAt,asc` page 0, which on any conversation longer than the
        // page size painted the OLDEST 200 messages — so on a long-running chat the very message
        // the operator had just been alerted about was the one guaranteed to be missing.
        const res = await api.get(`/api/v1/message/find-all`, {
            params: {chatId, page: 0, size: 200, sort: 'createdAt,desc'},
        });
        const rows = res?.data?.content || res?.content;
        if (Array.isArray(rows)) rows.reverse();
        return res;
    } catch (error) {
        console.error('❌ Error fetching messages for chat:', error);
        throw error;
    }
};


/**
 * The signed-in operator's own chat list, over plain HTTP.
 *
 * The socket remains the live path; this exists so a single missed STOMP frame can no longer
 * strand the operator on an empty inbox until they reload the page.
 */
export const fetchMyChatList = async () => {
    try {
        return await api.get(`/api/v1/chat/my-list`);
    } catch (error) {
        console.error('❌ Error fetching chat list:', error);
        throw error;
    }
};


export const fetchOperatorsAfterTime = async () => {
    try {
        return await api.get(`/api/v1/operator/shift-after-time`);
    } catch (error) {
        console.error('❌ Error fetching operators with future shifts:', error);
        throw error;
    }
};


export const transferChatApi = async (chatId, operatorId) => {
    try {
        return await api.post(`/api/v1/chat/transfer-chat/${chatId}/${operatorId}`);
    } catch (error) {
        console.error('❌ Error transferring chat:', error);
        throw error;
    }
};


/**
 * The pages a web visitor looked at around this conversation.
 *
 * <p>Returns an empty journey for Telegram chats rather than an error, so the caller never has to
 * branch on channel before asking.
 */
export const fetchVisitorJourney = async (chatId) => {
    const res = await api.get(`/api/v1/chat/${chatId}/journey`);
    return res?.data ?? res;
};

/**
 * Corrects the text of a message support already sent.
 *
 * <p>Only the id and the new text are sent. Everything that decides whether the edit is allowed —
 * who sent the message, which chat it belongs to, how old it is — is read from the database
 * server-side, so nothing here can influence permission.
 */
export const editMessageText = async (messageId, text) => {
    return await api.put(`/api/v1/message/${messageId}/text`, {text});
};
